import React, { useState, useEffect, useCallback } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import DateTimePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';

import { URL } from '../core/config';
import { setCurrentSemester } from '../core/store/actions/semester';
import { hasPermission } from '../core/utils/permissionUtils';
import SemesterService from '../core/services/SemesterService';
import NavHeader from './NavHeader';
import CustomAlert from './CustomAlert';
import CustomAlertWithButtons from './CustomAlertWithButtons';
import Avatar from './Avatar';
import '../styles/Form.css';
import '../styles/responsive.css';
import '../styles/CreateSemester.css';
import '../styles/SemesterManagement.css';

const formatDate = (date) => (date ? new Date(date).toLocaleDateString('en-GB') : '-');

function formatVolunteerName(user) {
  if (user.nickname && user.firstLastName) {
    return `${user.nickname} (${user.firstLastName.trim()})`;
  }
  return user.firstLastName?.trim() || user.fullName || 'Unknown User';
}

function SemesterManagement() {
  const history = useHistory();
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.profile);
  const canManageSemesters = hasPermission(userProfile, 'MANAGE_SEMESTERS');

  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertInfo, setAlertInfo] = useState({ show: false, type: '', message: '' });
  const [confirmActivate, setConfirmActivate] = useState(null); // semester being confirmed for activation
  const [confirmDelete, setConfirmDelete] = useState(null); // semester being confirmed for deletion

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ name: '', startDate: null, endDate: null, type: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const [editVolunteers, setEditVolunteers] = useState([]);
  const [editAddableVolunteers, setEditAddableVolunteers] = useState([]);
  const [editVolunteersLoading, setEditVolunteersLoading] = useState(false);
  const [editSelectedIds, setEditSelectedIds] = useState(new Set());
  const [editLockedIds, setEditLockedIds] = useState(new Set());
  const [editVolunteerSearch, setEditVolunteerSearch] = useState('');

  const showAlert = (type, message) => setAlertInfo({ show: true, type, message });

  const fetchSemesters = useCallback(() => {
    setLoading(true);
    SemesterService.getSemesters()
      .then((data) => setSemesters(data || []))
      .catch((error) => {
        console.error('Error fetching semesters:', error);
        showAlert('error', 'Could not load semesters.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (canManageSemesters) {
      fetchSemesters();
    }
  }, [canManageSemesters, fetchSemesters]);

  if (!canManageSemesters) {
    return <Redirect to="/main" />;
  }

  const startEditing = (semester) => {
    setEditingId(semester._id);
    setEditValues({
      name: semester.name || '',
      startDate: semester.startDate ? new Date(semester.startDate) : null,
      endDate: semester.endDate ? new Date(semester.endDate) : null,
      type: semester.type || '',
    });

    setEditVolunteers([]);
    setEditAddableVolunteers([]);
    setEditSelectedIds(new Set());
    setEditLockedIds(new Set());
    setEditVolunteerSearch('');
    setEditVolunteersLoading(true);

    const activeSemester = semesters.find((s) => s.isActive);
    const fetches = [
      axios.get(`${URL}/users/list`, { params: { semester: semester._id, includeSuspended: true } }),
      SemesterService.getLockedVolunteers(semester._id),
    ];

    // If there's an active semester other than the one being edited, offer its
    // members as candidates to add (they weren't previously imported here).
    if (activeSemester && activeSemester._id !== semester._id) {
      fetches.push(
        axios.get(`${URL}/users/list`, { params: { semester: activeSemester._id, includeSuspended: true } }),
      );
    }

    Promise.all(fetches)
      .then(([usersResponse, lockedResponse, activeUsersResponse]) => {
        const present = usersResponse.data || [];
        setEditVolunteers(present);
        setEditSelectedIds(new Set(present.map((user) => user._id)));
        setEditLockedIds(new Set(lockedResponse.userIds || []));

        if (activeUsersResponse) {
          const presentIds = new Set(present.map((user) => user._id));
          const addable = (activeUsersResponse.data || []).filter((user) => !presentIds.has(user._id));
          setEditAddableVolunteers(addable);
        }
      })
      .catch((error) => {
        console.error('Error fetching semester volunteers:', error);
        showAlert('error', 'Could not load this semester\'s volunteers.');
      })
      .finally(() => setEditVolunteersLoading(false));
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const toggleEditVolunteer = (userId) => {
    if (editLockedIds.has(userId) && editSelectedIds.has(userId)) {
      return;
    }
    setEditSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const matchesVolunteerSearch = (user) => {
    if (!editVolunteerSearch.trim()) return true;
    const query = editVolunteerSearch.trim().toLowerCase();
    return (
      (user.fullName || '').toLowerCase().includes(query) ||
      (user.nickname || '').toLowerCase().includes(query)
    );
  };

  const filteredEditVolunteers = editVolunteers.filter(matchesVolunteerSearch);
  const filteredAddableVolunteers = editAddableVolunteers.filter(matchesVolunteerSearch);

  const saveEditing = (semesterId) => {
    setSavingEdit(true);
    Promise.all([
      SemesterService.updateSemester(semesterId, editValues),
      SemesterService.updateSemesterVolunteers(semesterId, Array.from(editSelectedIds)),
    ])
      .then(() => {
        setEditingId(null);
        showAlert('success', 'Semester updated successfully.');
      })
      .catch((error) => {
        console.error('Error updating semester:', error);
        showAlert('error', error.message || 'There was an error updating the semester. Please try again.');
      })
      .finally(() => {
        setSavingEdit(false);
        fetchSemesters();
      });
  };

  const handleActivate = (semester) => {
    SemesterService.setActiveSemester(semester._id)
      .then((activated) => {
        dispatch(setCurrentSemester(activated));
        showAlert('success', `"${activated.name}" is now the active semester.`);
        fetchSemesters();
      })
      .catch((error) => {
        console.error('Error activating semester:', error);
        showAlert('error', 'There was an error activating the semester. Please try again.');
      })
      .finally(() => setConfirmActivate(null));
  };

  const handleDelete = (semester) => {
    SemesterService.deleteSemester(semester._id)
      .then(() => {
        setSemesters((prev) => prev.filter((s) => s._id !== semester._id));
        showAlert('success', `"${semester.name}" was deleted successfully.`);
      })
      .catch((error) => {
        console.error('Error deleting semester:', error);
        showAlert('error', error.message || 'There was an error deleting the semester. Please try again.');
      })
      .finally(() => setConfirmDelete(null));
  };

  return (
  <div className="page-container">
    <div className="semester-management-page">
      <NavHeader />
      <h1 className="page-title" style={{ marginTop: '10px' }}>Semester Management</h1>

      <div className="semester-management-actions">
        <button className="button" onClick={() => history.push('/semesters/new')}>
          <Icon icon="material-symbols:add-circle-outline" /><span>New Semester</span>
        </button>
      </div>

      {loading ? (
        <p style={{ textAlign: 'center' }}>Loading semesters...</p>
      ) : (
        <ul className="semester-management-list">
          {semesters.length === 0 ? (
            <li className="semester-management-empty">No semesters have been created yet.</li>
          ) : (
            semesters.map((semester) => (
              <li key={semester._id} className={`semester-management-item ${semester.isActive ? 'active' : ''}`}>
                {editingId === semester._id ? (
                  <div className="semester-management-edit-form">
                    <div className='edit-options-input'>
                    <div className='section'>
                      <div className="form-group">
                        <label>Name</label>
                        <input
                        className="form-input"
                        type="text"
                        value={editValues.name}
                        onChange={(e) => setEditValues((prev) => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Type</label>
                        <select
                          className="form-input"
                          value={editValues.type}
                          onChange={(e) => setEditValues((prev) => ({ ...prev, type: e.target.value }))}
                        >
                          <option value="" disabled>Select a type...</option>
                          <option value="Fall">Fall</option>
                          <option value="Spring">Spring</option>
                        </select>
                      </div>
                    </div>
                    <div className='section'>
                      <div className="form-group">
                        <label>Start Date</label>
                        <DateTimePicker
                        selected={editValues.startDate}
                        onChange={(date) => setEditValues((prev) => ({ ...prev, startDate: date }))}
                        dateFormat="dd/MM/yyyy"
                        className="form-input"
                        popperProps={{ strategy: 'fixed' }}
                        portalId="root-portal"
                        />
                      </div>
                      <div className="form-group">
                        <label>End Date</label>
                        <DateTimePicker
                        selected={editValues.endDate}
                        onChange={(date) => setEditValues((prev) => ({ ...prev, endDate: date }))}
                        dateFormat="dd/MM/yyyy"
                        className="form-input"
                        popperProps={{ strategy: 'fixed' }}
                        portalId="root-portal"
                        />
                      </div>
                    </div>
                    </div>

                    <div className="edit-volunteers-section">
                      <label>Volunteers</label>
                      <p className="edit-volunteers-hint">
                        Only members from the current active semester can be added here. To import
                        members from a different previous semester, set that semester as active
                        first — its members will then appear in this list.
                      </p>
                      {editVolunteersLoading ? (
                        <p>Loading volunteers...</p>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Search volunteers..."
                            value={editVolunteerSearch}
                            onChange={(e) => setEditVolunteerSearch(e.target.value)}
                          />
                          <ul className="semester-import-list">
                            {filteredEditVolunteers.length === 0 && filteredAddableVolunteers.length === 0 ? (
                              <li className="semester-import-empty-row">No volunteers found.</li>
                            ) : (
                              <>
                                {filteredEditVolunteers.map((user) => {
                                  const locked = editLockedIds.has(user._id) && editSelectedIds.has(user._id);
                                  return (
                                    <li
                                      key={user._id}
                                      className={`semester-import-item ${user.suspension?.isSuspended ? 'suspended-user' : ''}`}
                                    >
                                      <label className={`${editSelectedIds.has(user._id) ? 'selected' : ''} ${locked ? 'locked' : ''}`}>
                                        <input
                                          type="checkbox"
                                          checked={editSelectedIds.has(user._id)}
                                          disabled={locked}
                                          onChange={() => toggleEditVolunteer(user._id)}
                                        />
                                        <Avatar user={user} size={32} />
                                        <span className="semester-import-item-name">{formatVolunteerName(user)}</span>
                                        {locked && (
                                          <span className="organizer-badge" title="Organizes an event in this semester and cannot be removed">
                                            Has Event
                                          </span>
                                        )}
                                        {user.suspension?.isSuspended && <span className="suspended-badge">Suspended</span>}
                                      </label>
                                    </li>
                                  );
                                })}

                                {filteredAddableVolunteers.length > 0 && (
                                  <>
                                    <li className="semester-import-section-header">
                                      From the active semester (not yet added)
                                    </li>
                                    {filteredAddableVolunteers.map((user) => (
                                      <li
                                        key={user._id}
                                        className={`semester-import-item ${user.suspension?.isSuspended ? 'suspended-user' : ''}`}
                                      >
                                        <label className={editSelectedIds.has(user._id) ? 'selected' : ''}>
                                          <input
                                            type="checkbox"
                                            checked={editSelectedIds.has(user._id)}
                                            onChange={() => toggleEditVolunteer(user._id)}
                                          />
                                          <Avatar user={user} size={32} />
                                          <span className="semester-import-item-name">{formatVolunteerName(user)}</span>
                                          {user.suspension?.isSuspended && <span className="suspended-badge">Suspended</span>}
                                        </label>
                                      </li>
                                    ))}
                                  </>
                                )}
                              </>
                            )}
                          </ul>
                        </>
                      )}
                    </div>

                    <div className="semester-management-edit-actions">
                      <button className="button" disabled={savingEdit} onClick={() => saveEditing(semester._id)}>
                        Save
                      </button>
                      <button className="button" disabled={savingEdit} onClick={cancelEditing}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="semester-management-info">
                      <span className="semester-management-name">
                        {semester.name}
                        {semester.isActive && <span className="active-badge">Current</span>}
                      </span>
                      <span className="semester-management-details">
                        {semester.type} &middot; {formatDate(semester.startDate)} - {formatDate(semester.endDate)}
                      </span>
                    </div>
                    <div className="semester-management-item-actions">
                      {!semester.isActive && (
                        <button className="button list-button" onClick={() => setConfirmActivate(semester)}>
                          <Icon icon="nrk:check-active" />
                        </button>
                      )}
                      <button className="button list-button" onClick={() => startEditing(semester)}>
                        <Icon icon="boxicons:edit" />
                      </button>
                      <button className="button list-button" onClick={() => setConfirmDelete(semester)}>
                        <Icon icon="material-symbols:delete-outline" />
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))
          )}
        </ul>
      )}

      {confirmActivate && (
        <CustomAlertWithButtons
          title="Set as Active Semester?"
          type="warning"
          message={`This will deactivate all other semesters and make "${confirmActivate.name}" the active semester for every user. Continue?`}
          button1Text="Cancel"
          button2Text="Set Active"
          onButton1Click={() => setConfirmActivate(null)}
          onButton2Click={() => handleActivate(confirmActivate)}
        />
      )}

      {confirmDelete && (
        <CustomAlertWithButtons
          title="Delete Semester?"
          type="error"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This cannot be undone. Semesters with existing events or open calls cannot be deleted.`}
          button1Text="Cancel"
          button2Text="Delete"
          onButton1Click={() => setConfirmDelete(null)}
          onButton2Click={() => handleDelete(confirmDelete)}
        />
      )}

      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          autoDismiss={true}
          timeout={4000}
          onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
        />
      )}
    </div>
  </div>
  );
}

export default SemesterManagement;
